import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useSnackbar,
  useTable,
  useTableFetch,
  useTranslations,
  useTableLocalization,
  useFilterButton,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  IUseFilterButtonReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
  TSecurityScoreName,
  getSecurityScoreStrength,
} from '@uniqkey-frontend/shared-app';
import {
  ActivityFilter,
  EmployeeAccountOrderProperty,
  EmployeeAccountStatus,
  EmployeeAccountType,
  GetEmployeeAccountsResponseModel,
} from '@uniqkey-backend-organization-web/api-client';
import { useSearchParams } from 'react-router-dom';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import {
  IGetEmployeeAccountsParams,
  TGetEmployeeAccountsMethod,
} from '../../useEmployeeAccountsAPI/interfaces';
import {
  IEmployeesListFilterSubmitResult,
} from '../../../pages/EmployeesPage/components/EmployeesListFilter';
import { IEmployeesTableRow } from '../../../components/tables/EmployeesTable';
import useEmployeeAccountsAPI from '../../useEmployeeAccountsAPI';

interface IPersistentFilters extends Pick<IGetEmployeeAccountsParams, 'groupId'> {}

export interface IUseEmployeesTableProps {
  persistentFilters?: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUseEmployeesTableReturn extends
  Omit<IUseTableReturn<IEmployeesTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<IUseTableFetchReturn<GetEmployeeAccountsResponseModel>, 'data'>,
  IUseFilterButtonReturn
{
  employees: IUseTableFetchReturn<GetEmployeeAccountsResponseModel>['data'];
  selectedEmployees: IUseTableReturn<IEmployeesTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetEmployeeAccountsParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<IGetEmployeeAccountsParams['searchQuery']>>;
  filterValues: IEmployeesListFilterSubmitResult;
  setFilterValues: Dispatch<SetStateAction<IEmployeesListFilterSubmitResult>>;
}

interface ITablePreservationConfigs extends
  Omit<IGetEmployeeAccountsParams,
    'page' | 'pageLength' | 'groupId' | 'orderPropertyName' | 'isDescending'
    | 'maxStrength' | 'minStrength' | 'cancelToken'
  >,
  Pick<IUseTableReturn<IEmployeesTableRow>, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetEmployeeAccountsParams['page'];
  perPage: IGetEmployeeAccountsParams['pageLength'];
  securityScoreName: TSecurityScoreName;
  group: IEmployeesListFilterSubmitResult['group'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: EmployeeAccountOrderProperty },
  ),
  columnOrderDirection: true,
  typeFilter: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: EmployeeAccountType },
  ),
  statusFilter: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: EmployeeAccountStatus },
  ),
  securityScoreName: true,
  activityFilter: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: ActivityFilter },
  ),
  group: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.OBJECT,
  ),
};

const useEmployeesTable = (props: IUseEmployeesTableProps): IUseEmployeesTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = props;

  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const { getEmployeeAccounts } = useEmployeeAccountsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    typeFilter: initialTypeFilter,
    statusFilter: initialStatusFilter,
    securityScoreName: initialSecurityScoreName,
    searchQuery: initialSearchQuery,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
    activityFilter: initialActivityFilter,
    group: initialGroup,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetEmployeeAccountsParams['searchQuery']
  >(() => initialSearchQuery);
  const [filterValues, setFilterValues] = useState<
    IEmployeesListFilterSubmitResult
  >({
    typeFilter: initialTypeFilter,
    statusFilter: initialStatusFilter,
    securityScoreName: initialSecurityScoreName,
    activityFilter: initialActivityFilter,
    group: initialGroup,
  });

  const { isFilterActive, numberOfActiveFilters } = useFilterButton(filterValues);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedEmployees,
    resetTableMethods,
    ...restTableProps
  } = useTable<IEmployeesTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
    setFilterValues({
      typeFilter: undefined,
      statusFilter: undefined,
      securityScoreName: TablePreserverCommonInitialTableValues.securityScoreName,
      activityFilter: undefined,
      group: undefined,
    });
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: employees, isLoading, isFetchedAfterMount, total, refetch,
  } = useTableFetch<
    IGetEmployeeAccountsParams,
    TGetEmployeeAccountsMethod,
    GetEmployeeAccountsResponseModel
  >({
    request: getEmployeeAccounts,
    params: {
      page: activePage,
      pageLength: perPage,
      typeFilter: filterValues.typeFilter,
      statusFilter: filterValues.statusFilter,
      activityFilter: filterValues.activityFilter,
      groupId: filterValues.group?.value,
      ...getSecurityScoreStrength(filterValues.securityScoreName),
      searchQuery,
      orderPropertyName: EmployeeAccountOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof EmployeeAccountOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isFilterActive,
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
    typeFilter: filterValues.typeFilter,
    statusFilter: filterValues.statusFilter,
    securityScoreName: filterValues.securityScoreName,
    activityFilter: filterValues.activityFilter,
    group: {
      value: filterValues.group,
      parser: JSON.stringify,
    },
  });

  return {
    employees,
    isLoading,
    total,
    refetch,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedEmployees,
    localization,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default useEmployeesTable;
